import React, { useState } from "react";
import { 
  useMediaQuery, Button, Grid, TextField, Box, Typography, 
  Container, Stack, Backdrop, CircularProgress, Snackbar, IconButton 
} from "@mui/material";
import { Helmet } from "react-helmet";
import { contactus } from "../Model/Auction";
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';

const Contact = () => {
  
  // CSS para itago ang scrollbar indicator pero gumagana ang scroll
  const hideScrollbar = {
    msOverflowStyle: "none", 
    scrollbarWidth: "none", 
    "&::-webkit-scrollbar": {
      display: "none",
    },
  };

  const [inquireMessage, setInquireMessage] = useState("");
  const [openNotif, setOpenNotif] = useState(false);
  const [loading, setLoading] = useState(false);
  const [values, setValues] = useState({
    name: "", email: "", phone: "", subject: "", message: "",
  });

  const handleChange = (e) => {
    setValues({ ...values, [e.target.name]: e.target.value });
  };

  const sendHandle = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const results = await contactus(values);
      setInquireMessage(results.msg || "Message sent successfully.");
    } catch (error) {
      setInquireMessage(error.response?.data?.msg || "An error occurred");
    } finally {
      setLoading(false);
      setOpenNotif(true);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", pt: { xs: 6, md: 12 }, pb: 12, bgcolor: "#fff" }}>
      <Helmet>
        <title>Inquiries | Leon Gallery</title>
      </Helmet>

      <Container maxWidth="lg">
        
        <Grid container spacing={0} sx={{ alignItems: "flex-start" }}>
          
          
          <Grid 
            item xs={12} md={7} 
            sx={{ 
              pr: { md: 10 },
              maxHeight: { md: "85vh" }, // Limit sa height ng screen
              overflowY: { md: "auto" }, // Enable scroll
              ...hideScrollbar           // Hide scrollbar indicator
            }}
          >
            <Typography variant="h2" sx={{ 
                fontFamily: "'Playfair Display', serif", 
                fontWeight: 400, 
                color: "#000", 
                mb: 3, 
                fontSize: { xs: "2.8rem", md: "3.5rem" }
            }}>
              How can we help?
            </Typography>
            <Typography sx={{ color: "#333", mb: 8, fontSize: "1.1rem", fontWeight: 300, maxWidth: "500px" }}>
              Have a question or request? We’re here to help. Whether you’re interested in consigning, bidding, or learning more about Philippine art, our team is ready to assist you.
            </Typography>

            
            <form onSubmit={sendHandle}>
              <Grid container spacing={6}>
                {[
                  { name: "name", label: "FULL NAME", required: true },
                  { name: "email", label: "EMAIL ADDRESS", required: true, type: "email" },
                  { name: "phone", label: "PHONE NUMBER", value: values.phone },
                  { name: "subject", label: "SUBJECT" }
                ].map((field) => (
                  <Grid item xs={12} sm={6} key={field.name}>
                    <TextField 
                      fullWidth 
                      required={field.required}
                      name={field.name} 
                      label={field.label} 
                      type={field.type || "text"}
                      variant="standard" 
                      onChange={handleChange} 
                      InputLabelProps={{ sx: { fontSize: '0.7rem', letterSpacing: 2, fontWeight: 800, color: "#000" } }}
                      sx={{ 
                        "& .MuiInput-root:before": { borderBottomColor: "#000" }, 
                        "& .MuiInput-root:after": { borderBottomColor: "#b0966a" } 
                      }}
                    />
                  </Grid>
                ))}
                <Grid item xs={12}>
                  <TextField 
                    fullWidth required name="message" label="MESSAGE" multiline rows={4} variant="standard" 
                    onChange={handleChange}
                    InputLabelProps={{ sx: { fontSize: '0.7rem', letterSpacing: 2, fontWeight: 800, color: "#000" } }}
                    sx={{ "& .MuiInput-root:before": { borderBottomColor: "#000" } }}
                  />
                </Grid>
                <Grid item xs={12} sx={{ mt: 4 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    sx={{
                      bgcolor: "#000", color: "#fff", borderRadius: 0,
                      px: 10, py: 2.5, fontSize: "0.8rem", fontWeight: 700, letterSpacing: 2,
                      "&:hover": { bgcolor: "#333" }
                    }}
                  >
                    SUBMIT INQUIRY
                  </Button>
                </Grid>
              </Grid>
            </form>

            
            <Box sx={{ mt: 15, pt: 10, borderTop: "1px solid #eee", pb: 5 }}>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: 4, color: "#b0966a", mb: 2 }}>
                YOUR TREASURE, OUR EXPERTISE
              </Typography>
              <Typography variant="h3" sx={{ fontFamily: "'Playfair Display', serif", color: "#000", mb: 3 }}>
                Consign with Confidence
              </Typography>
              <Typography sx={{ color: "#333", lineHeight: 2, mb: 5, fontSize: "1.1rem" }}>
              Taking the step to consign your valued possessions is a significant decision. Leon Gallery Auctions provides a seamless selling experience, combining our expertise with your unique collection.
              </Typography>
              <Button
                variant="contained"
                href="/request"
                sx={{ bgcolor: "#000", color: "#fff", borderRadius: 0, px: 6, py: 2, fontWeight: 700, letterSpacing: 2 }}
              >
                REQUEST AN ESTIMATE
              </Button>
            </Box>
          </Grid>

          
          <Grid 
            item xs={12} md={5} 
            sx={{ 
              mt: { xs: 12, md: 0 }, 
              pl: { md: 10 }, 
              borderLeft: { md: "1px solid #000" },
              position: { md: "sticky" }, 
              top: { md: "120px" }, // Gaano kalayo sa top habang naka-stick
              alignSelf: "flex-start"
            }}
          >
            <Stack spacing={8}>
              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: "0.7rem", letterSpacing: 3, color: "#b0966a", mb: 4 }}>
                  OFFICE LOCATIONS
                </Typography>
                <Stack spacing={5}>
                  <Box>
                    <Typography sx={{ fontSize: "0.9rem", fontWeight: 800, color: "#000", mb: 1 }}>EUROVILLA 1 (MAIN GALLERY)</Typography>
                    <Typography sx={{ fontSize: "0.95rem", color: "#444", lineHeight: 1.6 }}>
                      G/F Eurovilla 1, Rufino corner Legazpi Street,<br />
                      Legazpi Village, Makati City, Philippines
                    </Typography>
                    <Typography sx={{ fontSize: "1rem", color: "#000", fontWeight: 700, mt: 1 }}>T: (02) 8856 2781</Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: "0.9rem", fontWeight: 800, color: "#000", mb: 1 }}>LA FUERZA</Typography>
                    <Typography sx={{ fontSize: "0.95rem", color: "#444", lineHeight: 1.6 }}>
                      G/F La Fuerza Building, Chino Roces Avenue,<br />
                      Makati City, Philippines
                    </Typography>
                    <Typography sx={{ fontSize: "1rem", color: "#000", fontWeight: 700, mt: 1 }}>T: 8772-8343</Typography>
                  </Box>
                </Stack>
              </Box>

              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: "0.7rem", letterSpacing: 2, color: "#999", mb: 2 }}>GENERAL INQUIRIES</Typography>
                <Typography component="a" href="mailto:info@leon-gallery.com" sx={{ 
                  color: "#1a1a1a", textDecoration: "none", fontSize: "1.2rem", fontFamily: "serif",
                  borderBottom: "1px solid #e0e0e0", pb: 0.5
                }}>
                  info@leon-gallery.com
                </Typography>
              </Box>

              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: "0.7rem", letterSpacing: 2, color: "#999", mb: 2 }}>CONNECT</Typography>
                <Stack direction="row" spacing={2}>
                  <IconButton size="small" href="#" sx={{ color: "#1a1a1a", p: 0 }}><FacebookIcon fontSize="small" /></IconButton>
                  <IconButton size="small" href="#" sx={{ color: "#1a1a1a", p: 0 }}><InstagramIcon fontSize="small" /></IconButton>
                  <IconButton size="small" href="#" sx={{ color: "#1a1a1a", p: 0 }}><YouTubeIcon fontSize="small" /></IconButton>
                </Stack>
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </Container>

      
      <Backdrop sx={{ color: "#fff", zIndex: 9999 }} open={loading}>
        <CircularProgress color="inherit" thickness={2} size={30} />
      </Backdrop>

      <Snackbar
        open={openNotif}
        autoHideDuration={6000}
        onClose={() => setOpenNotif(false)}
        message={inquireMessage}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{ "& .MuiSnackbarContent-root": { bgcolor: "#000", borderRadius: 0 } }}
      />
    </Box>
  );
};

export default Contact;